// Ein Auftrag (Screen hausmeister/03): Hero mit Adresse und Status, darunter Zeit, Fotos,
// Material, Abnahme, Fahrt und Notizen. Die Logik steckt unverändert in useAuftrag.
import { useEffect, useState, type CSSProperties } from 'react';
import {
  ArrowUpRight, Camera, CameraPlus, CarProfile, Check, CheckCircle, MapPin, NotePencil, Package,
  PaperPlaneRight, PenNib, Play, Plus, Signature, Stop, Timer, X,
} from '@phosphor-icons/react';
import { GlasWidget, SegmentSteuerung, farbKlasse } from '../../components/portal/bausteine';
import AbnahmeUnterschrift from '../../components/tickets/AbnahmeUnterschrift';
import type { HausmeisterTicket } from '../../lib/hausmeister/api';
import {
  STATUS_STUFEN, adresse, euro, kmText, kmWert, mapsLink, materialSumme, notizZeit, statusStufe, uhrzeit,
  type StatusStufe,
} from '../../lib/hausmeister/darstellung';
import { stundenText } from '../../lib/zeiterfassung/berechnung';
import { zeitMinuten } from '../../lib/zeiterfassung/ticketzeit';
import { STUFE_STIL } from './symbole';
import { useAuftrag } from './useAuftrag';

const ZEILE: CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', fontSize: 14 };

export default function AuftragDetail({ ticket, meineId, onFehler, onGeaendert }: {
  ticket: HausmeisterTicket;
  meineId: string;
  onFehler: (s: string | null) => void;
  onGeaendert: () => void;
}) {
  const a = useAuftrag(ticket, meineId, onFehler, onGeaendert);
  const [jetzt, setJetzt] = useState(() => new Date());
  const [notiz, setNotiz] = useState('');
  const [matName, setMatName] = useState('');
  const [matMenge, setMatMenge] = useState('1');
  const [matPreis, setMatPreis] = useState('');
  const [km, setKm] = useState('');
  const [abnahme, setAbnahme] = useState(false);
  const [sendet, setSendet] = useState<null | 'zeit' | 'foto' | 'material' | 'fahrt' | 'notiz'>(null);
  const [fotoArt, setFotoArt] = useState<'intern' | 'vorher' | 'nachher'>('intern');

  useEffect(() => {
    if (!a.timer) return;
    const i = setInterval(() => setJetzt(new Date()), 1000);
    return () => clearInterval(i);
  }, [a.timer]);

  const stufe = statusStufe(a.status);
  const stil = STUFE_STIL[stufe];
  const adr = adresse(ticket);
  const fotos = a.fotos.filter((f) => f.url);
  const summe = materialSumme(a.material);
  const kmSumme = a.fahrten.reduce((s, f) => s + Number(f.kilometer), 0);
  const kmOk = kmWert(km) !== null;
  const matOk = !!matName.trim();
  const notizOk = !!notiz.trim();
  const HeroSymbol = stil.icon;

  const mit = async (art: NonNullable<typeof sendet>, fn: () => Promise<void>) => {
    if (sendet) return;
    setSendet(art);
    try { await fn(); } finally { setSendet(null); }
  };

  return (
    <div className="p-rein" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Hero */}
      <div className={`p-stapel ${farbKlasse(stil.farbe)}`} style={{ paddingBottom: 12 }}>
        <div className={`p-kachel ${farbKlasse(stil.farbe)}`} style={{ borderRadius: 30, padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <HeroSymbol aria-hidden size={190} weight="fill" style={{ position: 'absolute', right: -24, top: -30, opacity: 0.16, transform: 'rotate(-12deg)', zIndex: 0 }} />
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <span className="p-auf-farbe" style={{ width: 52, height: 52, borderRadius: 16, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <HeroSymbol size={26} weight="fill" />
            </span>
            <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, lineHeight: 1.2, letterSpacing: '-0.015em', overflowWrap: 'anywhere' }}>{ticket.titel}</h1>
              {adr && (
                <a
                  href={mapsLink(adr)} target="_blank" rel="noreferrer" data-tip="In Google Maps öffnen"
                  style={{ fontSize: 13.5, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6, width: 'fit-content', maxWidth: '100%', color: 'var(--p-w)', padding: '6px 12px 6px 10px', borderRadius: 16, background: 'var(--p-s18)', boxShadow: 'inset 0 0 0 0.5px var(--p-w30)' }}
                >
                  <MapPin size={14} weight="fill" style={{ flexShrink: 0 }} />
                  <span style={{ overflowWrap: 'anywhere' }}>{adr}</span>
                  <ArrowUpRight size={11} weight="bold" style={{ opacity: 0.8, flexShrink: 0 }} />
                </a>
              )}
            </div>
          </div>
          {ticket.beschreibung && (
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: 'var(--p-w92)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{ticket.beschreibung}</p>
          )}
          <div style={{ marginTop: 4 }}>
            <SegmentSteuerung<StatusStufe>
              label="Status des Auftrags"
              art="hell"
              wert={stufe}
              gesperrt={a.arbeitet}
              onWahl={(k) => void a.statusWechseln(k)}
              optionen={STATUS_STUFEN.map((s) => ({ key: s.key, label: s.label, icon: STUFE_STIL[s.key].icon, tip: `Status auf „${s.label}“ setzen` }))}
            />
          </div>
        </div>
      </div>

      <div className="hm-detail-raster">
        {/* Zeit */}
        <GlasWidget farbe="timer" icon={Timer} titel="Zeit auf diesem Auftrag" style={{ gap: 14 }}>
          {a.timer ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
                <span className="p-zahl" style={{ fontSize: 40, fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--p-warn)', lineHeight: 1 }}>
                  {stundenText(zeitMinuten(a.timer, jetzt)).replace(' h', '')}
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--p-w45)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span className="p-puls" style={{ width: 7, height: 7, borderRadius: 4, background: 'var(--p-warn-punkt)' }} />
                  läuft seit {uhrzeit(a.timer.beginn)} Uhr
                </span>
              </div>
              <button
                type="button" className="p-knopf" disabled={sendet === 'zeit'}
                data-tip="Zeit stoppen – der Text im Notizfeld unten wird als Tätigkeit gespeichert"
                style={{ border: 'none', background: 'var(--p-w12)', padding: '0 20px' }}
                onClick={() => void mit('zeit', async () => { await a.zeitStoppen(notiz); setNotiz(''); })}
              >
                <Stop size={16} weight="fill" /> Stopp
              </button>
            </div>
          ) : (
            <button
              type="button" className="p-knopf p-knopf-gold" disabled={sendet === 'zeit'}
              data-tip="Zeiterfassung für diesen Auftrag starten" style={{ height: 52, borderRadius: 26, fontSize: 15 }}
              onClick={() => void mit('zeit', a.zeitStarten)}
            >
              <Play size={17} weight="fill" /> Zeit starten
            </button>
          )}
        </GlasWidget>

        {/* Fotos */}
        <GlasWidget farbe="blau" icon={Camera} titel="Fotos" style={{ gap: 14 }}
          rechts={fotos.length > 0 ? <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--p-w40)' }}>{fotos.length}</span> : undefined}>
          {/* #564: Vorher/Nachher-Fotos sieht der Eigentümer im Portal, „Nur intern“ nicht */}
          <SegmentSteuerung<'intern' | 'vorher' | 'nachher'> label="Nächstes Foto" wert={fotoArt} onWahl={setFotoArt} hoehe={36}
            optionen={[
              { key: 'intern', label: 'Nur intern', tip: 'Foto nur für die Verwaltung' },
              { key: 'vorher', label: 'Vorher', tip: 'Zustand vor der Arbeit – der Eigentümer sieht es im Portal' },
              { key: 'nachher', label: 'Nachher', tip: 'Ergebnis nach der Arbeit – der Eigentümer sieht es im Portal' },
            ]} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))', gap: 8 }}>
            {fotos.map((f) => (
              <a key={f.id} href={f.url} target="_blank" rel="noreferrer" data-tip={f.dateiname}
                style={{ aspectRatio: '1', borderRadius: 14, overflow: 'hidden', display: 'block', border: '1px solid var(--p-w08)', position: 'relative' }}>
                <img src={f.url} alt={f.dateiname} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                {f.foto_art && (
                  <span style={{ position: 'absolute', left: 6, bottom: 6, padding: '2px 7px', borderRadius: 8, fontSize: 10, fontWeight: 800, background: 'var(--p-ton-ok-bg)', color: 'var(--p-ton-ok-fg)' }}>
                    {f.foto_art === 'vorher' ? 'Vorher' : 'Nachher'}
                  </span>
                )}
              </a>
            ))}
            <label
              data-tip="Foto aufnehmen oder auswählen (max. 25 MB)"
              style={{ aspectRatio: '1', borderRadius: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, cursor: sendet === 'foto' ? 'progress' : 'pointer', border: '1.5px dashed var(--p-w20)', color: 'var(--p-w50)', fontSize: 11, fontWeight: 700, opacity: sendet === 'foto' ? 0.6 : 1 }}
            >
              <CameraPlus size={24} />
              {sendet === 'foto' ? 'Lädt …' : 'Foto'}
              <input
                type="file" accept="image/*" capture="environment" style={{ display: 'none' }} disabled={sendet === 'foto'}
                aria-label="Foto aufnehmen oder auswählen (max. 25 MB)"
                onChange={(e) => {
                  const d = e.target.files?.[0];
                  e.target.value = '';
                  if (!d) return;
                  void mit('foto', () => a.fotoHinzu(d, fotoArt === 'intern' ? null : fotoArt));
                }}
              />
            </label>
          </div>
        </GlasWidget>

        {/* Material */}
        <GlasWidget farbe="braun" icon={Package} titel="Material"
          rechts={summe > 0 ? <span className="p-zahl" style={{ fontSize: 12, fontWeight: 700, color: 'var(--p-w40)' }}>{euro(summe)}</span> : undefined}>
          {a.material.length > 0 && (
            <div className="p-mulde">
              {a.material.map((m) => (
                <div key={m.id} style={ZEILE}>
                  <span className="p-zahl" style={{ fontWeight: 700, color: 'var(--p-w50)', flexShrink: 0 }}>{Number(m.menge).toLocaleString('de-DE')} {m.einheit}</span>
                  <span className="p-kuerzen" style={{ flex: 1, fontWeight: 600 }}>{m.bezeichnung}</span>
                  {m.einzelpreis !== null && (
                    <span className="p-zahl" style={{ flexShrink: 0, color: 'var(--p-w45)', fontWeight: 600 }}>{euro(Number(m.menge) * Number(m.einzelpreis))}</span>
                  )}
                  {m.erfasst_von === meineId && (
                    <button type="button" className="p-rund" data-tip="Eigene Position löschen" aria-label={`${m.bezeichnung} löschen`}
                      style={{ width: 28, height: 28, background: 'transparent', color: 'var(--p-w35)' }}
                      onClick={() => void a.materialWeg(m.id)}>
                      <X size={13} weight="bold" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          <form
            style={{ display: 'flex', gap: 6 }}
            onSubmit={(e) => {
              e.preventDefault();
              void mit('material', async () => {
                if (await a.materialHinzu(matMenge, matName, matPreis)) { setMatName(''); setMatMenge('1'); setMatPreis(''); }
              });
            }}
          >
            <input className="p-feld" value={matMenge} onChange={(e) => setMatMenge(e.target.value)} inputMode="decimal"
              aria-label="Menge (Stk)" data-tip="Menge (Stk)" style={{ width: 56, height: 46, padding: 0, textAlign: 'center', fontWeight: 700 }} />
            <input className="p-feld" value={matName} onChange={(e) => setMatName(e.target.value)} placeholder="z. B. Dichtung"
              aria-label="Bezeichnung" data-tip="Bezeichnung" style={{ flex: 1, height: 46, padding: '0 12px' }} />
            <input className="p-feld" value={matPreis} onChange={(e) => setMatPreis(e.target.value)} inputMode="decimal" placeholder="€"
              aria-label="Einzelpreis (optional)" data-tip="Einzelpreis (optional)" style={{ width: 64, height: 46, padding: '0 10px' }} />
            <button type="submit" className={`p-rund ${matOk ? 'p-knopf-gold' : ''}`} disabled={!matOk || sendet === 'material'}
              data-tip="Material erfassen" aria-label="Material erfassen"
              style={{ width: 46, height: 46, borderRadius: 23, ...(matOk ? {} : { background: 'var(--p-w10)', color: 'var(--p-w50)', cursor: 'default' }) }}>
              <Check size={18} weight="bold" />
            </button>
          </form>
        </GlasWidget>

        {/* Abnahme */}
        <GlasWidget farbe="lila" icon={PenNib} titel={abnahme ? 'Abnahme vor Ort' : 'Abnahme'}
          rechts={abnahme ? (
            <button type="button" className="p-rund" data-tip="Abbrechen" aria-label="Abnahme abbrechen"
              style={{ width: 30, height: 30, color: 'var(--p-w70)' }} onClick={() => setAbnahme(false)}>
              <X size={13} weight="bold" />
            </button>
          ) : undefined}>
          {abnahme ? (
            <AbnahmeUnterschrift
              ticketId={ticket.id}
              meineId={meineId}
              dunkel
              onFertig={() => { setAbnahme(false); void a.laden(); }}
              onAbbruch={() => setAbnahme(false)}
              onFehler={onFehler}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {a.abnahmen.length > 0 && (
                <div className="p-mulde">
                  {a.abnahmen.map((x) => (
                    <div key={x.id} style={ZEILE}>
                      <CheckCircle size={18} weight="fill" style={{ color: 'var(--p-ok)', flexShrink: 0 }} />
                      <span className="p-kuerzen" style={{ flex: 1, fontWeight: 600 }}>{x.name}</span>
                      <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 600, color: 'var(--p-w40)' }}>{new Date(x.created_at).toLocaleDateString('de-DE')}</span>
                    </div>
                  ))}
                </div>
              )}
              <button type="button" className="p-knopf" data-tip="Abnahme vor Ort mit Unterschrift auf dem Display" onClick={() => setAbnahme(true)}>
                <Signature size={18} /> Unterschrift einholen
              </button>
            </div>
          )}
        </GlasWidget>

        {/* Fahrt */}
        <GlasWidget farbe="fahrt" icon={CarProfile} titel="Fahrt"
          rechts={a.fahrten.length > 0 ? <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--p-w50)' }}>{kmText(kmSumme)} gesamt</span> : undefined}>
          <form
            style={{ display: 'flex', gap: 8 }}
            onSubmit={(e) => {
              e.preventDefault();
              void mit('fahrt', async () => { if (await a.fahrtHinzu(km)) setKm(''); });
            }}
          >
            <div style={{ position: 'relative', width: 110, flexShrink: 0 }}>
              <input className="p-feld p-zahl" value={km} onChange={(e) => setKm(e.target.value)} inputMode="decimal" placeholder="0"
                aria-label="Gefahrene Kilometer" style={{ width: '100%', fontSize: 18, fontWeight: 800, paddingRight: 40 }} />
              <span aria-hidden style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 13, fontWeight: 700, color: 'var(--p-w40)', pointerEvents: 'none' }}>km</span>
            </div>
            <button type="submit" className="p-knopf" disabled={!kmOk || sendet === 'fahrt'}
              data-tip="Gefahrene Kilometer für heute zu diesem Auftrag speichern"
              style={{ flex: 1, border: 'none', background: 'var(--p-w10)' }}>
              <Plus size={16} /> Kilometer eintragen
            </button>
          </form>
        </GlasWidget>

        {/* Notizen */}
        <GlasWidget farbe="gelb" icon={NotePencil} titel="Notizen"
          rechts={<span data-tip="Notizen sieht auch die Verwaltung" style={{ fontSize: 12, fontWeight: 600, color: 'var(--p-w40)', whiteSpace: 'nowrap' }}>für die Verwaltung</span>}>
          {a.notizen.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {a.notizen.slice(0, 6).map((n) => (
                <div key={n.id} style={{ borderRadius: '16px 16px 16px 6px', padding: '10px 12px', background: 'var(--p-w07)', display: 'flex', flexDirection: 'column', gap: 3, maxWidth: '92%' }}>
                  <span style={{ fontSize: 14, lineHeight: 1.45, color: 'var(--p-w85)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{n.inhalt}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--p-w35)' }}>{notizZeit(n.created_at)}</span>
                </div>
              ))}
            </div>
          )}
          <form
            style={{ display: 'flex', gap: 8 }}
            onSubmit={(e) => {
              e.preventDefault();
              void mit('notiz', async () => { if (await a.notizHinzu(notiz)) setNotiz(''); });
            }}
          >
            <input className="p-feld" value={notiz} onChange={(e) => setNotiz(e.target.value)} placeholder="Was wurde gemacht?"
              aria-label="Notiz" style={{ flex: 1, borderRadius: 24, padding: '0 16px' }} />
            <button type="submit" className={`p-rund ${notizOk ? 'p-knopf-gold' : ''}`} disabled={!notizOk || sendet === 'notiz'}
              data-tip="Notiz senden" aria-label="Notiz senden"
              style={{ width: 48, height: 48, borderRadius: 24, ...(notizOk ? {} : { background: 'var(--p-w10)', color: 'var(--p-w50)', cursor: 'default' }) }}>
              <PaperPlaneRight size={18} weight="fill" />
            </button>
          </form>
        </GlasWidget>
      </div>
    </div>
  );
}
